/**
 * BYOK vault.
 *
 * Threat model: protect against drive-by JS that scrapes IndexedDB by passive
 * read; we wrap each API key with AES-GCM using a key derived from a
 * device-local secret stored in IndexedDB's `meta` store. This is NOT a
 * defence against a compromised app — anything that runs in our origin can
 * derive the same secret. The wrapper exists so dumps of `keys` store alone
 * are useless, and so future hardware-backed unlock can drop in.
 *
 * Keys are never sent to the client bundle, never to public envs. They are
 * passed to /api/proxy via Authorization: Bearer at request time and
 * forwarded by the Netlify function to the upstream provider.
 */

import { db, type Provider } from './db';

const ALGO = { name: 'AES-GCM', length: 256 } as const;

async function getDeviceSecret(): Promise<CryptoKey> {
  const d = await db();
  const rec = await d.get('meta', 'device-secret');
  let raw: ArrayBuffer;
  if (rec && rec.value instanceof ArrayBuffer) {
    raw = rec.value;
  } else {
    raw = crypto.getRandomValues(new Uint8Array(32)).buffer;
    await d.put('meta', { id: 'device-secret', value: raw });
  }
  return crypto.subtle.importKey('raw', raw, ALGO, false, ['encrypt', 'decrypt']);
}

export async function saveKey(provider: Provider, apiKey: string, label?: string): Promise<void> {
  const key = await getDeviceSecret();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(apiKey));
  const d = await db();
  await d.put('keys', { provider, cipher, iv: iv.buffer, createdAt: Date.now(), label });
}

export async function getKey(provider: Provider): Promise<string | null> {
  const d = await db();
  const rec = await d.get('keys', provider);
  if (!rec) return null;
  const key = await getDeviceSecret();
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(rec.iv) }, key, rec.cipher);
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

export async function hasKey(provider: Provider): Promise<boolean> {
  const d = await db();
  return !!(await d.get('keys', provider));
}

export async function deleteKey(provider: Provider): Promise<void> {
  const d = await db();
  await d.delete('keys', provider);
}

export async function listProvidersWithKeys(): Promise<Provider[]> {
  const d = await db();
  const all = await d.getAll('keys');
  return all.map(r => r.provider);
}
